import { collectAllPages, delay, parseAwsJson } from "@examspark/common";
import {
  AuthorizationContextInput,
  CreateExamSessionInput,
  CreateExamSessionResult,
  ExamScore,
  ExamSession,
  IDSK,
  ModelResourceConnection,
  Replace,
  Resource,
  SearchableResourceConnectionNew,
  SearchableResourceFilterInput,
  UpdateExamSessionInput,
} from "@examspark/types";

import { GraphQLAPI } from "../../client";
import * as fragments from "../../fragments/resource";
import { getResource, listResources } from "../resource";

export const getExamSessionPublic = async (
  params: Replace<IDSK, { sk: "exam-session" }>,
) => {
  return GraphQLAPI.query<{ getExamSession: ExamSession }>({
    query: /* GraphQL */ `
      query GetExamSession($input: ModelGetInput!) {
        getExamSession(input: $input) {
          id
          sk
          name
          maturity
          examSessionResponses
          currentAttemptNumber
        }
      }
    `,
    variables: {
      input: params,
    },
    auth: "API_KEY",
  }).then((result) => result.data?.getExamSession ?? null);
};

export const listExamSessions = async (
  examConfigId: string,
  courseId: string,
) => {
  const result = await GraphQLAPI.client.query<{
    listResources: ModelResourceConnection;
  }>({
    query: /* GraphQL */ `
      query ListExamSessions($id: ID!, $sk: ModelStringKeyConditionInput!) {
        listResources(id: $id, sk: $sk) {
          items {
            id
            sk
            name
            maturity
            examSessionResponses
            examSessionResults
            resource {
              id
              sk
              name
            }
          }
        }
      }
    `,
    variables: {
      id: examConfigId,
      sk: {
        beginsWith: `exam-config-session`,
      },
    },
  });

  return result.data?.listResources;
};

export const listExamConfigSessions = async (params: {
  id: string;
  limit?: number;
}) => {
  return await listResources({
    label: "GetExamConfigProperties",
    variables: {
      ...params,
      sk: {
        beginsWith: "exam-config-session",
      },
    },
    fields: `
      items {
        ${fragments.id}
        ${fragments.basic}
        resource {
          ${fragments.id}
          ${fragments.basic}
          ${fragments.meta}
        }
      }
    `,
  });
};

export const listExamConfigSessionsWithResponses = async (params: {
  id: string;
  limit?: number;
}) => {
  return await listResources({
    label: "GetExamConfigPropertiesWithResponses",
    variables: {
      ...params,
      sk: {
        beginsWith: "exam-config-session",
      },
    },
    fields: `
      items {
        ${fragments.id}
        ${fragments.basic}
        resource {
          ${fragments.id}
          ${fragments.basic}
          ${fragments.meta}
          examSessionResponses
        }
      }
    `,
  });
};

type ExamScoreError = {
  userErrorMessage: {
    message: string;
    type: string;
  };
};
export const getStudentScore = async (params: {
  token: string;
  attemptNumber?: number;
}) => {
  return await GraphQLAPI.query<{
    getStudentScore: string;
  }>({
    query: /* GraphQL */ `
      query GetStudentScore($input: ControllerInput!) {
        getStudentScore(input: $input)
      }
    `,
    variables: {
      input: {
        action: "fetch-results",
        params: JSON.stringify(params),
      },
    },
  }).then((res) => {
    return parseAwsJson<ExamScore | ExamScoreError>(res?.data?.getStudentScore);
  });
};

export const pollStudentScore = async (params: {
  token: string;
  attemptNumber?: number;
}) => {
  // arbtirary delay. In testing, it almost always came back in the 1st or 2nd try
  // 2025-01-23 update: we're seeing some pretty long delays in production so I'm increasing
  // the timeout to 15 seconds
  let tries = 15;
  while (tries-- > 0) {
    await delay(1000);

    const studentScoreData = await getStudentScore(params);

    if (!studentScoreData) {
      throw new Error("Invalid Response from Score Lambda");
    }

    if ("userErrorMessage" in studentScoreData) {
      if (studentScoreData.userErrorMessage.type === "session-unscored") {
        continue;
      }
      throw new Error(studentScoreData.userErrorMessage.message);
    }
    return studentScoreData;
  }

  throw new Error("Score not available");
};

export const getStudentAnswerKey = async (params: {
  token: string;
}): Promise<Array<Resource | undefined> | undefined> => {
  return await GraphQLAPI.query<{
    getStudentAnswerKey: Resource[];
  }>({
    query: /* GraphQL */ `
      query GetStudentAnswerKey($input: ControllerInput!) {
        getStudentAnswerKey(input: $input)
      }
    `,
    variables: {
      input: {
        action: "fetch-answers",
        params: JSON.stringify(params),
      },
    },
  })
    .then((res) => res?.data?.getStudentAnswerKey.map(parseAwsJson<Resource>))
    .catch((err) => {
      // I noticed that an exam could be launched with an exam config that allows
      // fetching the answer key, but then it be disabled later which causes the
      // call to fail. This prevents blowing up the rest of the handling.
      console.error(err);
      return [];
    });
};

export const createExamSession = async (
  params: Replace<CreateExamSessionInput, { sk: "exam-session" }>,
) => {
  return GraphQLAPI.query<{ createExamSessionV2: CreateExamSessionResult }>({
    query: /* GraphQL */ `
      mutation CreateExamSession($input: CreateExamSessionInput!) {
        createExamSessionV2(input: $input) {
          created
          error
          examSession {
            id
            sk
            createdAt
            updatedAt
          }
        }
      }
    `,
    variables: {
      input: params,
    },
    auth: "API_KEY",
  }).then((res) => res?.data?.createExamSessionV2);
};

export const sendExamSessionUpdate = async (params: UpdateExamSessionInput) => {
  return GraphQLAPI.query<{ sendExamSessionUpdate: ExamSession }>({
    query: /* GraphQL */ `
      mutation SendExamSessionUpdate(
        $id: ID
        $examConfigId: ID
        $input: UpdateExamSessionInput!
      ) {
        sendExamSessionUpdate(
          id: $id
          examConfigId: $examConfigId
          input: $input
        ) {
          id
          sk
          examConfigId
          maturity

          name

          scoreMin
          scoreMax
          scoreActual

          currentAttemptNumber
          scoredAttemptNumber

          examPosition {
            form
            question
            section
          }

          isActive
        }
      }
    `,
    variables: {
      id: params.id,
      examConfigId: params.examConfigId,
      input: params,
    },
    auth: "API_KEY",
  }).then((res) => res?.data?.sendExamSessionUpdate);
};

export const updateExamSession = async (
  params: Replace<UpdateExamSessionInput, { sk: "exam-session" }>,
) => {
  return GraphQLAPI.query<{ updateExamSession: IDSK }>({
    query: /* GraphQL */ `
      mutation UpdateExamSession($input: UpdateExamSessionInput!) {
        updateExamSession(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: params,
    },
    auth: "API_KEY",
  }).then((res) => res?.data?.updateExamSession);
};

export const onUpdateExamSession = async () => {
  const query = await GraphQLAPI.query({
    query: /* GraphQL */ `
      subscription OnUpdateExamSession($id: ID!) {
        onUpdateExamSession(id: $id) {
          id
          sk
          name
          maturity
        }
      }
    `,
  });

  return query.data?.sub;
};

type SubmitStudentExamParams = Replace<
  UpdateExamSessionInput,
  { sk: "exam-session" }
> & { examConfigId: string };

export const finishExamSession = async (params: SubmitStudentExamParams) => {
  return GraphQLAPI.query<{
    submitStudentExam: {
      token: string;
      userErrorMessage: {
        type: string;
        message: string;
      };
    };
  }>({
    query: /* GraphQL */ `
      mutation SubmitStudentExam($input: ControllerInput!) {
        submitStudentExam(input: $input) {
          token
          userErrorMessage {
            type
            message
          }
        }
      }
    `,
    variables: {
      input: {
        action: "finish-exam",
        params: JSON.stringify(params),
      },
    },
  }).then((res) => res?.data?.submitStudentExam);
};

export const fetchFinishedExamSession = async (
  params: SubmitStudentExamParams,
) => {
  return GraphQLAPI.query<{ submitStudentExam: { token: string } }>({
    query: /* GraphQL */ `
      mutation SubmitStudentExam($input: ControllerInput!) {
        submitStudentExam(input: $input) {
          token
        }
      }
    `,
    variables: {
      input: {
        action: "fetch-finished-exam-token",
        params: JSON.stringify(params),
      },
    },
  }).then((res) => res?.data?.submitStudentExam);
};

export const updateExamSessionPrivate = async (
  params: Replace<UpdateExamSessionInput, { sk: "exam-session" }>,
) => {
  return GraphQLAPI.query<{ updateExamSession: IDSK }>({
    query: /* GraphQL */ `
      mutation UpdateExamSession($input: UpdateExamSessionInput!) {
        updateExamSession(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: params,
    },
  }).then((res) => res?.data?.updateExamSession);
};

export const updateExamSessionResponse = async (
  params: UpdateExamSessionInput,
) => {
  return GraphQLAPI.query<{ updateResource: IDSK }>({
    query: /* GraphQL */ `
      mutation UpdateExamSessionResponse($input: UpdateResourceInput!) {
        updateResource(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: params,
    },
  }).then((res) => res?.data?.updateResource);
};

export const fetchExamSessions = async (
  examConfigId: string,
  workspaceId: string,
) => {
  const pages = await collectAllPages(async (nextToken) => {
    const filter: SearchableResourceFilterInput = {
      sk: {
        eq: "exam-session",
      },
      workspaceId: {
        matchPhrasePrefix: workspaceId,
      },
      examConfigId: {
        eq: examConfigId,
      },
    };
    const result = await GraphQLAPI.query<{
      searchUserResources: SearchableResourceConnectionNew;
    }>({
      query: /* GraphQL */ `
        query SearchSessionsForExam(
          $filter: SearchableResourceFilterInput
          $sort: [SearchableResourceSortInput!]
          $aggs: [SearchableResourceAggsInput!]
          $limit: Int
          $nextToken: String
        ) {
          searchUserResources(
            filter: $filter
            sort: $sort
            aggs: $aggs
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              idInc
              idVendor
              sk
              type
              version
              bankId
              name
              maturity
              schemaVersion
              createdAt
              updatedAt
              workspaceId
              examSessionResponses
              examSessionResults
              imageUrl
              imageHash
            }
            nextToken
          }
        }
      `,
      variables: {
        filter,
        nextToken,
        sort: [
          {
            field: "createdAt",
          },
        ],
      },
    });
    return result.data?.searchUserResources;
  });

  return pages.flatMap((page) => page.items ?? []) ?? [];
};

export const listExamSessionResponses = async (examSessionId: string) => {
  return await listResources({
    label: "ListExamSessionResponses",
    variables: {
      id: examSessionId,
      sk: {
        beginsWith: "exam-response",
      },
    },
    fields: `
      items {
        id
        sk
        scoreMax
        scoreMin
        scoreActual
        resourceId
      }
    `,
  });
};

export const getExamSessionResponse = async (
  examSessionId: string,
  itemId: string,
) => {
  return (
    await getResource({
      label: "GetItemDataForPlayer",
      variables: {
        id: examSessionId,
        sk: `exam-response:${itemId}/item#v0`,
      },
      fields: `
        id
        idInc
        idVendor
        sk
        scoreMax
        scoreActual
        scoreMin
        resource {
          id
          sk
          name
          idInc
          examSessionResponses
        }
        ${fragments.alignments}
        ${fragments.tags}

      `,
    })
  ).data?.getResource;
};

export const resetUserStudentCount = async (
  params: AuthorizationContextInput,
) => {
  return GraphQLAPI.query<{ resetUserStudentCount: IDSK }>({
    query: /* GraphQL */ `
      mutation ResetUserStudentCount($input: AuthorizationContextInput!) {
        resetUserStudentCount(input: $input) {
          id
          sk
        }
      }
    `,
    variables: {
      input: params,
    },
  }).then((res) => res?.data?.resetUserStudentCount);
};
